import React, { useEffect, useState } from 'react';
import { Package, Briefcase, ShoppingCart, Clock, CheckCircle, FileText, TrendingUp, AlertCircle } from 'lucide-react';
import { fetchStats } from '../../api/supplierApi';

const STAT_CARDS = (s, tr) => [
  { label: tr ? 'Ürünler' : 'Products',  value: s.total_products,    icon: Package,     color: 'bg-amber-100 text-amber-600' },
  { label: tr ? 'Hizmetler' : 'Services', value: s.total_services,    icon: Briefcase,   color: 'bg-sky-100 text-sky-600' },
  { label: tr ? 'Siparişler' : 'Orders',  value: s.total_orders,      icon: ShoppingCart, color: 'bg-purple-100 text-purple-600' },
  { label: tr ? 'Bekleyen' : 'Pending',   value: s.pending_orders,    icon: Clock,       color: 'bg-orange-100 text-orange-600' },
  { label: tr ? 'Devam Eden' : 'In Progress', value: s.in_progress_orders, icon: TrendingUp, color: 'bg-blue-100 text-blue-600' },
  { label: tr ? 'Tamamlanan' : 'Completed',   value: s.completed_orders,   icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600' },
  { label: tr ? 'Belgeler' : 'Documents', value: s.total_documents,  icon: FileText,    color: 'bg-rose-100 text-rose-600' },
];

const ORDER_STATUS_LABEL = { pending: 'Bekliyor', acknowledged: 'Alındı', in_progress: 'Devam Ediyor', completed: 'Tamamlandı', cancelled: 'İptal' };
const ORDER_STATUS_COLOR = { pending: 'bg-orange-100 text-orange-700', acknowledged: 'bg-blue-100 text-blue-700', in_progress: 'bg-purple-100 text-purple-700', completed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700' };

export default function DashboardSection({ tr }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    fetchStats()
      .then(setStats)
      .catch(() => setError(tr ? 'İstatistikler yüklenemedi.' : 'Failed to load stats.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); 

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center gap-3 text-red-600 bg-red-50 rounded-2xl p-6">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-5 w-5" /> <span>{error}</span>
      </div>
      <button
        onClick={load}
        className="px-4 py-1.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors"
      >
        {tr ? 'Tekrar Dene' : 'Retry'}
      </button>
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">{tr ? 'Genel Bakış' : 'Dashboard'}</h2>
        <p className="text-sm text-gray-500">{tr ? 'Tüm aktivitelerinizin özeti.' : 'Overview of your activity.'}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {STAT_CARDS(stats, tr).map(({ label, value, icon: Icon, color }) => {
          const isPending = label === (tr ? 'Bekleyen' : 'Pending');
          const highlight = isPending && value > 0;
          return (
            <div key={label}
              className={`bg-white rounded-2xl shadow-sm border p-4 flex flex-col gap-3 relative ${highlight ? 'border-orange-300 ring-2 ring-orange-200' : 'border-gray-100'}`}
              data-testid={isPending ? 'supplier-stat-pending' : undefined}
            >
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
              {highlight && (
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse shadow-sm">
                  <span className="h-1 w-1 rounded-full bg-white" />
                  {tr ? 'Yeni' : 'New'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-amber-500" />
            {tr ? 'Son Siparişler' : 'Recent Orders'}
          </h3>
          {stats.recent_orders.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">{tr ? 'Henüz sipariş yok.' : 'No orders yet.'}</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_orders.map(o => {
                const isNew = ['quote_pending', 'pending', 'acknowledged'].includes(o.status)
                  && o.created_at && (Date.now() - new Date(o.created_at).getTime()) < 24 * 60 * 60 * 1000;
                return (
                <div key={o.id} className={`flex items-center justify-between py-2 border-b border-gray-50 last:border-0 ${isNew ? 'bg-orange-50/60 -mx-2 px-2 rounded-lg' : ''}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800">{o.order_title}</p>
                      {isNew && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse">
                          {tr ? 'Yeni' : 'New'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{o.order_number} · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ORDER_STATUS_COLOR[o.status] || 'bg-gray-100 text-gray-600'}`}>
                    {tr ? (ORDER_STATUS_LABEL[o.status] || o.status) : o.status}
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Products */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Package className="h-4 w-4 text-amber-500" />
            {tr ? 'Son Eklenen Ürünler' : 'Recent Products'}
          </h3>
          {stats.recent_products.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">{tr ? 'Henüz ürün yok.' : 'No products yet.'}</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_products.map(p => (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  {p.images?.[0] ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`} alt={p.title} className="h-10 w-10 rounded-xl object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Package className="h-5 w-5 text-amber-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.title}</p>
                    <p className="text-xs text-gray-400">{p.category || (tr ? 'Kategori yok' : 'No category')}</p>
                  </div>
                  <span className="text-sm font-bold text-amber-600">{p.currency} {(() => {
                    const pr = p.pricing || {};
                    let v = null;
                    for (const k of ['event_duration','one_time','daily','weekly','monthly']) { if (pr[k] != null) { v = pr[k]; break; } }
                    return parseFloat(v ?? p.price ?? 0).toFixed(2);
                  })()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
